/**
 * The phone link as this computer sees it (W10 phone pill, W04 session, P1 standby). One small store per tab:
 * calling (seconds left), linked (the standby pairing the sim service keeps between tasks), driving (a session's phone
 * is connected), the link quality, and which phone the next call goes to. The linked pairing and the picked phone
 * survive a reload (sessionStorage: the link belongs to this browser tab's session, not the account).
 */
import type { Device } from '@robotensor/api'
import { useSyncExternalStore } from 'react'

export interface PhoneLink {
  /** A call is ringing: the seconds left and the phone it rings. */
  calling: { secondsLeft: number; device: string | null } | null
  /** The phone stays linked between tasks (P1): the standby pairing to pass to the next session. */
  linked: { pairingId: string; since: number; device: string | null } | null
  /** A session is running with the phone connected. */
  driving: boolean
  /** Phone input lag as the session reports it. */
  latencyMs: number | null
  /** The phone the next call goes to ("Use another phone"); null = the default. */
  pickedDeviceId: string | null
}

const KEY = 'rt.phoneLink'
const EMPTY: PhoneLink = { calling: null, linked: null, driving: false, latencyMs: null, pickedDeviceId: null }

function load(): PhoneLink {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return EMPTY
    const p = JSON.parse(raw) as Partial<PhoneLink>
    return { ...EMPTY, linked: p.linked ?? null, pickedDeviceId: p.pickedDeviceId ?? null }
  } catch {
    return EMPTY
  }
}

let state: PhoneLink = typeof window === 'undefined' ? EMPTY : load()
const subs = new Set<() => void>()

function set(patch: Partial<PhoneLink>) {
  const next = { ...state, ...patch }
  if ((Object.keys(next) as (keyof PhoneLink)[]).every((k) => JSON.stringify(next[k]) === JSON.stringify(state[k]))) return // nothing changed: no re-render
  state = next
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ linked: state.linked, pickedDeviceId: state.pickedDeviceId }))
  } catch {
    /* private mode: the link lasts until the page closes */
  }
  for (const s of subs) s()
}

export const phoneLink = {
  get: (): PhoneLink => state,
  subscribe(cb: () => void): () => void {
    subs.add(cb)
    return () => subs.delete(cb)
  },
  calling: (secondsLeft: number | null, device: string | null = null) => set({ calling: secondsLeft === null ? null : { secondsLeft, device } }),
  linked: (pairingId: string, device: string | null) => set({ linked: { pairingId, since: state.linked?.pairingId ? state.linked.since : Date.now(), device } }),
  unlink: () => set({ linked: null }),
  driving: (on: boolean, latencyMs: number | null = null) => set({ driving: on, latencyMs }),
  pick: (deviceId: string | null) => set({ pickedDeviceId: deviceId }),
  /** Tests. */
  reset: () => set(EMPTY),
}

export function usePhoneLink(): PhoneLink {
  return useSyncExternalStore(phoneLink.subscribe, phoneLink.get, () => EMPTY)
}

/** Phones of the person's that can drive a robot: every active one. A development build (the web preview) drives
 * too, as practice, so it counts; real phones come first, each kind most recently seen first. */
export function phonesOf(devices: readonly Device[] | undefined): Device[] {
  const seen = (d: Device) => (d.last_seen_at ? Date.parse(d.last_seen_at) || 0 : 0)
  const active = (devices ?? []).filter((d) => d.status === 'active').sort((a, b) => seen(b) - seen(a))
  return [...active.filter((d) => d.platform !== 'dev'), ...active.filter((d) => d.platform === 'dev')]
}

/** The phone the next call rings: the one picked in the pill menu, else the default, else the most recently seen one
 * that answers calls, else the most recently seen ready phone. A phone set up again (a reinstall) leaves its old
 * record behind, still answering calls until it's replaced: the record in use is the one seen last (as the platform
 * picks when no phone is named, control/calls.py). */
export function phoneFor(devices: readonly Device[] | undefined, picked: string | null): Device | undefined {
  const phones = phonesOf(devices)
  return (
    phones.find((d) => d.id === picked) ??
    phones.find((d) => d.default_controller) ??
    phones.find((d) => d.answer_calls) ??
    phones[0]
  )
}

export function phoneName(d: Pick<Device, 'model' | 'platform'> | null | undefined): string {
  if (!d) return 'your phone'
  return d.model ?? (d.platform === 'ios' ? 'iPhone' : d.platform === 'android' ? 'Android phone' : 'your phone')
}

/** "Good · 38 ms" from the session's input lag. */
export function quality(ms: number | null | undefined): { label: 'good' | 'fair' | 'weak'; text: string } {
  if (ms === null || ms === undefined) return { label: 'good', text: 'Good' }
  if (ms <= 120) return { label: 'good', text: `Good · ${ms} ms` }
  if (ms <= 300) return { label: 'fair', text: `Fair · ${ms} ms` }
  return { label: 'weak', text: `Weak · ${ms} ms` }
}

export type PillState = 'none' | 'ready' | 'calling' | 'linked' | 'driving'

export function pillState(link: PhoneLink, phone: Device | undefined): PillState {
  if (link.calling) return 'calling'
  if (link.driving) return 'driving'
  if (link.linked) return 'linked'
  return phone ? 'ready' : 'none'
}

/** The pill's words: No phone · Pixel 8 ready · Calling · 47 · Linked · Driving. */
export function pillText(s: PillState, link: PhoneLink, phone: Device | undefined): string {
  switch (s) {
    case 'calling':
      return `Calling · ${link.calling?.secondsLeft ?? 0}`
    case 'driving':
      return 'Driving'
    case 'linked':
      return 'Linked'
    case 'ready':
      return `${phoneName(phone)} ready`
    default:
      return 'No phone'
  }
}
